import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import log from 'loglevel';

import { useInboxEntryCommentsSlice } from './useInboxEntryCommentsSlice';
import { useGetPaginatedCommentsByInboxEntry } from '../../services/inboxEntryComments.service';
import { COMMENTS_PAGE_LIMIT } from '../../utils/constants';

export const useFetchInboxEntryComments = (inboxEntryId: string) => {
  const { t } = useTranslation();
  const { getPaginatedCommentsByInboxEntry } =
    useGetPaginatedCommentsByInboxEntry();

  const {
    exclusiveStartKey,
    hasMore,
    isLoadingMore,
    initEntry,
    clearEntry,
    setComments,
    appendComments,
    setLoading,
    setLoadingMore,
    setError,
  } = useInboxEntryCommentsSlice(inboxEntryId);

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPaginatedCommentsByInboxEntry({
        inboxEntryId,
        limit: COMMENTS_PAGE_LIMIT,
      });
      setComments(result.comments, result.exclusiveStartKey);
    } catch (error) {
      log.error(
        `Error fetching comments for inbox entry ${inboxEntryId}:`,
        error,
      );
      setError(t('components.inboxEntryDetailsModal.comments.fetchError'));
      setLoading(false);
    }
  }, [
    t,
    inboxEntryId,
    getPaginatedCommentsByInboxEntry,
    setComments,
    setLoading,
    setError,
  ]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;

    setLoadingMore(true);
    try {
      const result = await getPaginatedCommentsByInboxEntry({
        inboxEntryId,
        limit: COMMENTS_PAGE_LIMIT,
        exclusiveStartKey,
      });
      appendComments(result.comments, result.exclusiveStartKey);
    } catch (error) {
      log.error(
        `Error loading more comments for inbox entry ${inboxEntryId}:`,
        error,
      );
      setError(t('components.inboxEntryDetailsModal.comments.fetchError'));
      setLoadingMore(false);
    }
  }, [
    t,
    inboxEntryId,
    hasMore,
    isLoadingMore,
    exclusiveStartKey,
    getPaginatedCommentsByInboxEntry,
    appendComments,
    setLoadingMore,
    setError,
  ]);

  useEffect(() => {
    initEntry();
    fetchInitial();

    return () => {
      clearEntry();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxEntryId]);

  return { loadMore };
};
